import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Building2, Minus, Plus, ImageIcon, X } from 'lucide-react';

const STUDIO_TYPES = [
  { id: 'premium', name: 'Studio Premium', defaultPrice: 3500, color: 'from-amber-500 to-yellow-500', images: [
    'https://customer-assets-eiarnc6j.emergentagent.net/job_74fff74c-28bf-46c8-bab4-be3f33fd5449/artifacts/f0yn7a00_PHOTO_LOCATION_LQ-10.JPG',
    'https://customer-assets-eiarnc6j.emergentagent.net/job_74fff74c-28bf-46c8-bab4-be3f33fd5449/artifacts/arl4g3rs_PHOTO_LOCATION_LQ-7.JPG',
    'https://customer-assets-eiarnc6j.emergentagent.net/job_74fff74c-28bf-46c8-bab4-be3f33fd5449/artifacts/z0w4zqpn_PHOTO_LOCATION_LQ-8.JPG',
    'https://customer-assets-eiarnc6j.emergentagent.net/job_74fff74c-28bf-46c8-bab4-be3f33fd5449/artifacts/kukkcap2_PHOTO_LOCATION_LQ-5.JPG',
  ]},
  { id: 'essential', name: 'Studio Essential', defaultPrice: 2000, color: 'from-blue-500 to-cyan-500', images: [
    'https://customer-assets-eiarnc6j.emergentagent.net/job_74fff74c-28bf-46c8-bab4-be3f33fd5449/artifacts/csmjs4ek_PHOTO_LOCATION_LQ-11.JPG',
  ]},
];

function HoursControl({ value, onChange, testIdPrefix }) {
  return (
    <div className="flex items-center gap-1">
      <button onClick={() => onChange(Math.max(0, value - 0.5))} className="w-7 h-7 rounded bg-white/10 hover:bg-white/20 flex items-center justify-center text-slate-400 hover:text-white transition-colors" data-testid={`${testIdPrefix}-decrease`}>
        <Minus className="w-3 h-3" />
      </button>
      <span className="w-10 text-center text-white font-bold text-sm" data-testid={`${testIdPrefix}-hours`}>{value}h</span>
      <button onClick={() => onChange(value + 0.5)} className="w-7 h-7 rounded bg-white/10 hover:bg-white/20 flex items-center justify-center text-slate-400 hover:text-white transition-colors" data-testid={`${testIdPrefix}-increase`}>
        <Plus className="w-3 h-3" />
      </button>
    </div>
  );
}

function StudioCard({ studio, hours, onHoursChange, price, formatPrice, currency }) {
  const [showCarousel, setShowCarousel] = useState(false);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const total = price * hours;
  const images = studio.images || [];

  return (
    <div className={`rounded-xl border transition-all ${hours > 0 ? 'border-[#aa2aff]/40 bg-[#aa2aff]/5' : 'border-white/10 bg-white/[0.02]'}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h4 className={`font-semibold text-sm ${hours > 0 ? 'text-white' : 'text-slate-300'}`}>{studio.name}</h4>
            <p className="text-xs text-[#aa2aff] font-semibold">{formatPrice(price)} {currency} / hora</p>
          </div>
          <HoursControl value={hours} onChange={onHoursChange} testIdPrefix={`studio-${studio.id}`} />
        </div>
        {hours > 0 && (
          <p className="text-xs text-green-400 font-bold">{hours}h × {formatPrice(price)} = {formatPrice(total)} {currency}</p>
        )}
        {images && images.length > 0 && (
          <button onClick={() => setShowCarousel(!showCarousel)} className="mt-2 text-xs text-[#00c2ff] hover:underline flex items-center gap-1">
            <ImageIcon className="w-3 h-3" /> Ver fotos del estudio ({images.length})
          </button>
        )}
        {showCarousel && images.length > 0 && (
          <div className="mt-3 relative group">
            <img src={images[carouselIdx]} alt={`${studio.name} ${carouselIdx + 1}`} className="w-full h-48 object-cover rounded-lg" />
            {images.length > 1 && (
              <>
                <button onClick={() => setCarouselIdx((carouselIdx - 1 + images.length) % images.length)} className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">‹</button>
                <button onClick={() => setCarouselIdx((carouselIdx + 1) % images.length)} className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">›</button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {images.map((_, i) => (
                    <button key={i} onClick={() => setCarouselIdx(i)} className={`w-2 h-2 rounded-full transition-colors ${i === carouselIdx ? 'bg-white' : 'bg-white/40'}`} />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function StudioFeesSection({ isOpen, onToggle, studioHours, onStudioHoursChange, audioPostHours, onAudioPostHoursChange, formatPrice, currency, settings = {} }) {
  const premiumPrice = settings.price_studio_premium || 3500;
  const essentialPrice = settings.price_studio_essential || 2000;
  const audioPostPrice = settings.price_audio_post || 1500;

  const premiumTotal = (studioHours.premium || 0) * premiumPrice;
  const essentialTotal = (studioHours.essential || 0) * essentialPrice;
  const audioPostTotal = (audioPostHours || 0) * audioPostPrice;
  const grandTotal = premiumTotal + essentialTotal + audioPostTotal;

  return (
    <div className="border border-white/10 rounded-xl overflow-hidden bg-gradient-to-b from-[#1a1f50]/50 to-[#080c2d]/50" data-testid="studio-fees-section">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors" data-testid="btn-toggle-studio-fees">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <span className="text-white font-semibold text-lg block">Studio Fees</span>
            {grandTotal > 0 && (
              <span className="text-xs font-bold text-green-400">Total: {formatPrice(grandTotal)} {currency}</span>
            )}
          </div>
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>
      {isOpen && (
        <div className="p-4 pt-0 space-y-3">
          {STUDIO_TYPES.map(st => (
            <StudioCard
              key={st.id}
              studio={st}
              hours={studioHours[st.id] || 0}
              onHoursChange={(h) => onStudioHoursChange({ ...studioHours, [st.id]: h })}
              price={st.id === 'premium' ? premiumPrice : essentialPrice}
              formatPrice={formatPrice}
              currency={currency}
            />
          ))}
          {/* AudioPost */}
          <div className={`rounded-xl border p-4 transition-all ${audioPostHours > 0 ? 'border-[#aa2aff]/40 bg-[#aa2aff]/5' : 'border-white/10 bg-white/[0.02]'}`}>
            <div className="flex items-center justify-between mb-1">
              <div>
                <h4 className={`font-semibold text-sm ${audioPostHours > 0 ? 'text-white' : 'text-slate-300'}`}>AudioPost</h4>
                <p className="text-xs text-[#aa2aff] font-semibold">{formatPrice(audioPostPrice)} {currency} / hora</p>
              </div>
              <HoursControl value={audioPostHours} onChange={onAudioPostHoursChange} testIdPrefix="studio-audio-post" />
            </div>
            {audioPostHours > 0 && (
              <p className="text-xs text-green-400 font-bold">{audioPostHours}h × {formatPrice(audioPostPrice)} = {formatPrice(audioPostTotal)} {currency}</p>
            )}
          </div>
          {grandTotal > 0 && (
            <div className="flex justify-between items-center p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <span className="text-white font-semibold text-sm">Subtotal Studio Fees</span>
              <span className="text-amber-400 font-bold text-lg">{formatPrice(grandTotal)} {currency}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default StudioFeesSection;
